/**
 * QuizInstructions component
 *
 * Description:
 * Displays quiz instructions and allows navigation to previous califications or to the next view.
 *
 * Features:
 * - Shows instructions and description for the quiz.
 * - Allows toggling between instructions and previous califications.
 * - Provides navigation buttons for ratings and next view.
 * - Uses Material-UI for UI elements.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import PrevCalifications from '../PrevCalifications/PrevCalifications';
import classes from './QuizInstructions.module.css';

const QuizInstructions = props => {
  const { t } = useTranslation(['lesson']);
  // Local state for califications view toggle
  const [calificationsView, setCalificationsView] = useState(false);

  /**
   * Handles toggling between instructions and previous califications.
   */
  const chanveViewHandler = () => {
    setCalificationsView(!calificationsView);
  };

  return (
    <>
      {/* Custom prop: 'className' for quiz container styling */}
      <div className={classes.quizContainer}>
        <aside className={classes.instructionsContainer}>
          {!calificationsView ? (
            <>
              <p className={classes.instructions__title}>
                {t('instructions')}:
              </p>
              <p className={classes.instructions__text}>
                {props.description}
              </p>
            </>
          ) : (
            <PrevCalifications
              // Custom prop: 'califications' passes previous califications data
              califications={props.preCalifications}
            />
          )}
        </aside>
      </div>
      {/* Custom prop: 'className' for button container styling */}
      <aside className={classes.btnsContainer}>
        {props.preCalifications && (
          <Button
            // Custom prop: 'onClick' for toggling view
            onClick={chanveViewHandler}
            className={classes.nextBtn}
          >
            {!calificationsView ? (
              <>
                {t('view_ratings')} <AssignmentIcon />
              </>
            ) : (
              <>
                <ChevronLeftIcon /> {t('back')}
              </>
            )}
          </Button>
        )}
        {!calificationsView && (
          <Button
            // Custom prop: 'onClick' for next view
            onClick={props.changeView}
            className={classes.nextBtn}
          >
            {t('next')} <ChevronRightIcon />
          </Button>
        )}
      </aside>
    </>
  );
};

QuizInstructions.propTypes = {
  /**
   * Description text for quiz instructions
   */
  description: PropTypes.string,
  /**
   * Course ID (not used directly here)
   */
  idCourse: PropTypes.number,
  /**
   * Array of previous califications
   */
  preCalifications: PropTypes.array,
  /**
   * Function to change the view (e.g., go to next step)
   */
  changeView: PropTypes.func
};

QuizInstructions.defaultProps = {
  description: '',
  idCourse: undefined,
  preCalifications: [],
  changeView: undefined
};

export default QuizInstructions;
